import { Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import TrialExpiredModal from '../../Components/Modals/TrialExpiredModal';

const BASE_URL = process.env.REACT_APP_API_URL;


function LoginPage() {
  const [formData, setFormData] = useState({
    emailAddress: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.emailAddress) newErrors.emailAddress = 'Email address is required';
    if (!formData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpgrade = () => {
    // Navigate to pricing page or payment flow
    navigate('/pricing');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    // Validation
    if (!validateForm()) {
      return;
    }
  
    setLoading(true);
  
    try {
      const response = await fetch(`${BASE_URL}/login`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        if (data.message === "Trial period expired.") {
          setShowTrialModal(true);
          setLoading(false);
          return;
        }
        throw new Error(data.message || "Login failed");
      }
  
      setLoading(false);
      setMessage(data.message || "Login successful!");
  
      // Store user data in localStorage

      const userData={
        id: data.user.id,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.emailAddress,
          role: data.user.role,
          plan: data.user.plan, // Store the user's plan if needed
          company:data.user.company,
          CompanyCode:data.user.companyCode,
          BranchCode:data.user.BranchCode,
          accountType:data.user.accountType,
          trialRemainingDays: data.user.trialRemainingDays 
      }
     
        localStorage.setItem('user', JSON.stringify(userData));
        // Show success alert

      setTimeout(() => {
        
  
        // Navigate based on role
        if (userData.role === "Branch admin" && userData.accountType === "individual")  {
          navigate("/IndividualDashboard");
        }
        if (userData.role === "Company admin" && userData.accountType === "business")  {
          navigate("/BusinessDashboard");
        }

      }, 1500);
        

  
    } catch (error) {
      setLoading(false);
      setMessage(`Error: ${error.message || "Failed to login. Please try again."}`);
    }
  };

  return (
    <>
      <Container 
        className="d-flex justify-content-center align-items-center" 
        style={{ height: '80vh' }}
      >
        <Row className="w-100 justify-content-center">
          <Col md={5}>
            <Card className="shadow-lg border-0 rounded-lg mb-4" style={{ maxWidth: '400px', margin: 'auto' }}>
              <Card.Header className="bg-primary text-white text-center py-3">
                <h3 className="mb-0">Login</h3>
                <p className="text-white-50 mb-0">Access your account</p>
              </Card.Header>
              <Card.Body className="p-4">
                {message && (
                  <Alert variant={message.startsWith("Error") ? "danger" : "success"} dismissible>
                    {message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                  {/* Email Address */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Email Address</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                          <Form.Control
                            type="email"
                            name="emailAddress"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            isInvalid={!!errors.emailAddress}
                            placeholder="Enter email address"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.emailAddress}
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Password */}
                  <Row className="mb-4">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Password</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <FaLock />
                          </span>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter password"
                            required
                            isInvalid={!!errors.password}
                            className="form-control"
                          />
                          <button
                            type="button"
                            className="input-group-text"
                            onClick={togglePasswordVisibility}
                            style={{ cursor: 'pointer' }}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Submit Buttons */}
                  <div className="d-grid gap-2">
                    <Button type="submit" variant="primary" size="m" disabled={loading}>
                      {loading ? "Logging in..." : "Login as Admin"}
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="m" 
                      onClick={() => navigate('/auditor-login')}
                    >
                      Login as Auditor
                    </Button>
                  </div>
                </Form>
              </Card.Body>
              <Card.Footer className="text-center py-3 bg-light">
                <p className="mb-0">
                  Don't have an account? <a href="/RegisterTabs" className="fw-bold">Register</a>
                </p>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      <TrialExpiredModal 
        show={showTrialModal}
        onHide={() => setShowTrialModal(false)}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}

export default LoginPage;
